import { useEffect, useState } from "react";
import {
  adminGetSmtp,
  adminSaveSmtp,
  adminTestSmtp,
  adminListUsers,
  adminUpdateUser,
  adminDeleteUser,
  adminListVouchers,
  adminCreateVoucher,
  adminDeleteVoucher,
  adminGetPaymentSettings,
  adminSavePaymentSettings,
  adminListEftPayments,
  adminConfirmEftPayment,
  adminRejectEftPayment,
} from "@/api";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { Loader2, Send, Save, Trash2, ShieldCheck, ShieldOff, Ticket, Plus, CreditCard, CheckCircle2, Banknote, XCircle } from "lucide-react";

export default function AdminPanel() {
  const { user } = useAuth();
  if (user && typeof user === "object" && user.role !== "admin") {
    return <Navigate to="/studio" replace />;
  }
  return (
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-10">
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold mb-1">
          Admin
        </p>
        <h1 className="font-display text-4xl md:text-5xl text-stone-900">
          Studio settings
        </h1>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="users" data-testid="admin-tab-users">
            Users
          </TabsTrigger>
          <TabsTrigger value="smtp" data-testid="admin-tab-smtp">
            Email (SMTP)
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments">
            Payments
          </TabsTrigger>
          <TabsTrigger value="eft" data-testid="admin-tab-eft">
            EFT Payments
          </TabsTrigger>
          <TabsTrigger value="vouchers" data-testid="admin-tab-vouchers">
            Vouchers
          </TabsTrigger>
        </TabsList>
        <TabsContent value="users">
          <UsersPanel />
        </TabsContent>
        <TabsContent value="smtp">
          <SmtpPanel />
        </TabsContent>
        <TabsContent value="payments">
          <PaymentsPanel />
        </TabsContent>
        <TabsContent value="eft">
          <EftPaymentsPanel />
        </TabsContent>
        <TabsContent value="vouchers">
          <VouchersPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UsersPanel() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const { user: me } = useAuth();

  async function reload() {
    setBusy(true);
    try {
      const r = await adminListUsers();
      setRows(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    reload();
  }, []);

  async function toggleAdmin(u) {
    const nextRole = u.role === "admin" ? "user" : "admin";
    try {
      await adminUpdateUser(u.id, { role: nextRole });
      toast.success(`${u.email} is now ${nextRole}`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function cancelSub(u) {
    try {
      await adminUpdateUser(u.id, { subscription_status: "canceled" });
      toast.success(`Subscription canceled for ${u.email}`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function del(u) {
    const ok = await confirm({
      title: "Delete user?",
      description: `Permanently delete ${u.email}? Their projects will be orphaned.`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await adminDeleteUser(u.id);
      toast.success(`Deleted ${u.email}`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  if (rows === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl overflow-hidden"
      data-testid="admin-users-panel"
    >
      <table className="w-full text-sm">
        <thead className="bg-stone-50">
          <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-stone-500">
            <th className="px-4 py-3 font-semibold">User</th>
            <th className="px-4 py-3 font-semibold">Role</th>
            <th className="px-4 py-3 font-semibold">Subscription</th>
            <th className="px-4 py-3 font-semibold">Trial ends</th>
            <th className="px-4 py-3 font-semibold text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr
              key={u.id}
              data-testid={`admin-user-row-${u.id}`}
              className="border-t border-stone-100"
            >
              <td className="px-4 py-3">
                <div className="text-stone-900 font-medium">{u.name || "—"}</div>
                <div className="text-xs text-stone-500">{u.email}</div>
              </td>
              <td className="px-4 py-3">
                <span
                  className={
                    u.role === "admin"
                      ? "text-[#C27A62] text-xs font-semibold uppercase tracking-wider"
                      : "text-stone-600 text-xs uppercase tracking-wider"
                  }
                >
                  {u.role}
                </span>
              </td>
              <td className="px-4 py-3">
                <span
                  className={
                    u.subscription_status === "active"
                      ? "text-emerald-700 text-xs font-medium"
                      : u.subscription_status === "trialing"
                      ? "text-amber-700 text-xs font-medium"
                      : "text-stone-500 text-xs font-medium"
                  }
                >
                  {u.subscription_status}
                </span>
              </td>
              <td className="px-4 py-3 text-stone-600 text-xs">
                {u.trial_ends_at ? new Date(u.trial_ends_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-4 py-3 text-right space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleAdmin(u)}
                  data-testid={`admin-toggle-role-${u.id}`}
                  disabled={busy || u.id === me?.id}
                  className="rounded-full"
                >
                  {u.role === "admin" ? (
                    <>
                      <ShieldOff className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                      Demote
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                      Make admin
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => cancelSub(u)}
                  data-testid={`admin-cancel-sub-${u.id}`}
                  disabled={busy || u.role === "admin"}
                  className="rounded-full"
                >
                  Cancel sub
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => del(u)}
                  data-testid={`admin-delete-${u.id}`}
                  disabled={busy || u.id === me?.id}
                  className="rounded-full text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-10 text-center text-stone-400">
                No users yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SmtpPanel() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  async function load() {
    try {
      const s = await adminGetSmtp();
      setForm({
        smtp_host: s.smtp_host || "",
        smtp_port: s.smtp_port || 587,
        smtp_username: s.smtp_username || "",
        smtp_password: s.smtp_password || "",
        smtp_use_tls: s.smtp_use_tls !== false,
        smtp_use_ssl: !!s.smtp_use_ssl,
        smtp_from_email: s.smtp_from_email || "",
        smtp_from_name: s.smtp_from_name || "Atelier Studio",
        _password_set: s.smtp_password_set,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    setBusy(true);
    try {
      const payload = { ...form };
      // If user did not touch the masked placeholder, drop it from request
      if (payload.smtp_password === "••••••••") delete payload.smtp_password;
      await adminSaveSmtp(payload);
      toast.success("SMTP settings saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    if (!testEmail) {
      toast.error("Enter an email to send a test to");
      return;
    }
    setBusy(true);
    try {
      await adminTestSmtp(testEmail);
      toast.success(`Test email sent to ${testEmail}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!form) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-5 max-w-2xl"
      data-testid="admin-smtp-panel"
    >
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
            SMTP host
          </label>
          <Input
            value={form.smtp_host}
            onChange={(e) => update("smtp_host", e.target.value)}
            data-testid="smtp-host"
            placeholder="smtp.gmail.com"
            className="h-10"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
            Port
          </label>
          <Input
            type="number"
            value={form.smtp_port}
            onChange={(e) => update("smtp_port", Number(e.target.value) || 587)}
            data-testid="smtp-port"
            className="h-10"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
            Username
          </label>
          <Input
            value={form.smtp_username}
            onChange={(e) => update("smtp_username", e.target.value)}
            data-testid="smtp-username"
            placeholder="apikey or email"
            className="h-10"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
            Password
          </label>
          <Input
            type="password"
            value={form.smtp_password}
            onChange={(e) => update("smtp_password", e.target.value)}
            data-testid="smtp-password"
            placeholder={form._password_set ? "•••••••• (saved)" : "App password"}
            className="h-10"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
            From email
          </label>
          <Input
            type="email"
            value={form.smtp_from_email}
            onChange={(e) => update("smtp_from_email", e.target.value)}
            data-testid="smtp-from-email"
            placeholder="hello@studio.com"
            className="h-10"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
            From name
          </label>
          <Input
            value={form.smtp_from_name}
            onChange={(e) => update("smtp_from_name", e.target.value)}
            data-testid="smtp-from-name"
            placeholder="Atelier Studio"
            className="h-10"
          />
        </div>
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input
            type="checkbox"
            checked={form.smtp_use_tls}
            onChange={(e) => update("smtp_use_tls", e.target.checked)}
            data-testid="smtp-use-tls"
          />
          STARTTLS
        </label>
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input
            type="checkbox"
            checked={form.smtp_use_ssl}
            onChange={(e) => update("smtp_use_ssl", e.target.checked)}
            data-testid="smtp-use-ssl"
          />
          SSL/TLS (port 465)
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-stone-100">
        <Button
          onClick={save}
          disabled={busy}
          data-testid="smtp-save"
          className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
          ) : (
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
          )}
          Save settings
        </Button>

        <div className="flex items-center gap-2 ml-auto">
          <Input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Send test to…"
            data-testid="smtp-test-email"
            className="h-10 w-64"
          />
          <Button
            onClick={sendTest}
            disabled={busy}
            data-testid="smtp-send-test"
            variant="outline"
            className="rounded-full"
          >
            <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Send test
          </Button>
        </div>
      </div>
    </div>
  );
}


function VouchersPanel() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    kind: "discount",
    discount_type: "percent",
    discount_value: 20,
    trial_extension_days: 14,
    max_redemptions: "",
  });
  const confirm = useConfirm();

  async function load() {
    try {
      const r = await adminListVouchers();
      setRows(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function create() {
    if (!form.code || form.code.trim().length < 3) {
      toast.error("Voucher code must be at least 3 characters");
      return;
    }
    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name?.trim() || null,
      kind: form.kind,
      max_redemptions: form.max_redemptions
        ? Number(form.max_redemptions)
        : null,
    };
    if (form.kind === "discount") {
      payload.discount_type = form.discount_type;
      payload.discount_value = Number(form.discount_value);
    } else if (form.kind === "trial_extension") {
      payload.trial_extension_days = Number(form.trial_extension_days);
    }
    setBusy(true);
    try {
      await adminCreateVoucher(payload);
      toast.success(`Voucher ${payload.code} created`);
      setForm({
        code: "",
        name: "",
        kind: "discount",
        discount_type: "percent",
        discount_value: 20,
        trial_extension_days: 14,
        max_redemptions: "",
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function del(v) {
    const ok = await confirm({
      title: "Delete voucher?",
      description: `Delete ${v.code}? Existing redemptions are retained.`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await adminDeleteVoucher(v.id);
      toast.success(`Deleted voucher ${v.code}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  function voucherValueLabel(v) {
    if (v.kind === "trial_extension") return `+${v.trial_extension_days} trial days`;
    // discount (and legacy percent_off/amount_off)
    const dtype = v.discount_type || (v.percent_off ? "percent" : "amount");
    const val = v.discount_value ?? v.percent_off ?? v.amount_off ?? v.amount_off_usd;
    if (dtype === "percent") return `${val}% off`;
    return `${val} off`;
  }

  function voucherKindLabel(v) {
    return v.kind === "trial_extension" ? "Trial Extension" : "Discount";
  }

  return (
    <div className="space-y-6" data-testid="admin-vouchers-panel">
      <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-4 max-w-3xl">
        <div className="flex items-center gap-2">
          <Ticket className="w-4 h-4 text-stone-600" strokeWidth={1.5} />
          <h3 className="font-display text-xl text-stone-900">Create voucher</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Code
            </label>
            <Input
              value={form.code}
              onChange={(e) => update("code", e.target.value.toUpperCase())}
              data-testid="voucher-code"
              placeholder="WELCOME20"
              className="h-10"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Name (optional)
            </label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              data-testid="voucher-name"
              placeholder="Welcome offer"
              className="h-10"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Kind
            </label>
            <Select value={form.kind} onValueChange={(v) => update("kind", v)}>
              <SelectTrigger data-testid="voucher-kind" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="discount">Discount</SelectItem>
                <SelectItem value="trial_extension">Trial Extension</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Max redemptions (optional)
            </label>
            <Input
              type="number"
              value={form.max_redemptions}
              onChange={(e) => update("max_redemptions", e.target.value)}
              data-testid="voucher-max-redemptions"
              placeholder="∞"
              className="h-10"
            />
          </div>
        </div>

        {form.kind === "discount" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Discount type
              </label>
              <Select
                value={form.discount_type}
                onValueChange={(v) => update("discount_type", v)}
              >
                <SelectTrigger data-testid="voucher-discount-type" className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percent off (%)</SelectItem>
                  <SelectItem value="amount">Amount off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                {form.discount_type === "percent" ? "Percent off" : "Amount off"}
              </label>
              <Input
                type="number"
                value={form.discount_value}
                onChange={(e) => update("discount_value", e.target.value)}
                data-testid="voucher-discount-value"
                min={1}
                max={form.discount_type === "percent" ? 100 : undefined}
                className="h-10"
              />
            </div>
          </div>
        )}

        {form.kind === "trial_extension" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Trial extension days
              </label>
              <Input
                type="number"
                value={form.trial_extension_days}
                onChange={(e) => update("trial_extension_days", e.target.value)}
                data-testid="voucher-trial-days"
                min={1}
                className="h-10"
              />
            </div>
          </div>
        )}

        <div className="pt-2 border-t border-stone-100">
          <Button
            onClick={create}
            disabled={busy}
            data-testid="voucher-create"
            className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
            ) : (
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
            )}
            Create voucher
          </Button>
        </div>
      </div>

      <div
        className="bg-white border border-stone-200 rounded-2xl overflow-hidden"
        data-testid="admin-vouchers-table"
      >
        <table className="w-full text-sm">
          <thead className="bg-stone-50">
            <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-stone-500">
              <th className="px-4 py-3 font-semibold">Code</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold">Value</th>
              <th className="px-4 py-3 font-semibold">Redemptions</th>
              <th className="px-4 py-3 font-semibold">Created</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center">
                  <Loader2 className="w-5 h-5 animate-spin text-stone-400 inline" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-stone-400">
                  No vouchers yet.
                </td>
              </tr>
            ) : (
              rows.map((v) => (
                <tr
                  key={v.id}
                  data-testid={`voucher-row-${v.code}`}
                  className="border-t border-stone-100"
                >
                  <td className="px-4 py-3">
                    <div className="font-mono text-stone-900 font-semibold">
                      {v.code}
                    </div>
                    {v.name && (
                      <div className="text-xs text-stone-500">{v.name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs uppercase tracking-wider text-stone-600">
                    {v.kind.replace("_", " ")}
                  </td>
                  <td className="px-4 py-3 text-stone-900">
                    {voucherValueLabel(v)}
                  </td>
                  <td className="px-4 py-3 text-stone-600 text-xs">
                    {v.redemptions || 0}
                    {v.max_redemptions ? ` / ${v.max_redemptions}` : ""}
                  </td>
                  <td className="px-4 py-3 text-stone-500 text-xs">
                    {v.created_at
                      ? new Date(v.created_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => del(v)}
                      data-testid={`voucher-delete-${v.code}`}
                      className="rounded-full text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SecretField({ label, testid, value, set, onChange, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
        {label}
      </label>
      <Input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        placeholder={set ? "•••••••• (saved)" : placeholder || ""}
        className="h-10"
      />
    </div>
  );
}

function PaymentsPanel() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const data = await adminGetPaymentSettings();
      setS(data);
      setForm({
        live_mode: !!data.live_mode,
        currency: data.currency || "ZAR",
        subscription_price: data.subscription_price ?? 99,
        payfast_enabled: !!data.payfast_enabled,
        paypal_enabled: !!data.paypal_enabled,
        eft_enabled: !!data.eft_enabled,
        eft_reference_mode: data.eft_reference_mode || "generated",
        eft_bank_name: data.eft_bank_name || "",
        eft_account_name: data.eft_account_name || "",
        eft_account_number: data.eft_account_number || "",
        eft_branch_code: data.eft_branch_code || "",
        eft_account_type: data.eft_account_type || "",
        eft_swift: data.eft_swift || "",
        payfast_sandbox_merchant_id: "",
        payfast_sandbox_merchant_key: "",
        payfast_sandbox_passphrase: "",
        payfast_live_merchant_id: "",
        payfast_live_merchant_key: "",
        payfast_live_passphrase: "",
        paypal_sandbox_client_id: "",
        paypal_sandbox_secret: "",
        paypal_live_client_id: "",
        paypal_live_secret: "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    setBusy(true);
    try {
      const payload = {
        live_mode: form.live_mode,
        currency: form.currency,
        subscription_price: Number(form.subscription_price) || 0,
        billing_interval: "one_time",
        payfast_enabled: form.payfast_enabled,
        paypal_enabled: form.paypal_enabled,
        eft_enabled: form.eft_enabled,
        eft_reference_mode: form.eft_reference_mode,
        eft_bank_name: form.eft_bank_name,
        eft_account_name: form.eft_account_name,
        eft_account_number: form.eft_account_number,
        eft_branch_code: form.eft_branch_code,
        eft_account_type: form.eft_account_type,
        eft_swift: form.eft_swift,
      };
      // Only send secret fields the admin actually typed
      [
        "payfast_sandbox_merchant_id",
        "payfast_sandbox_merchant_key",
        "payfast_sandbox_passphrase",
        "payfast_live_merchant_id",
        "payfast_live_merchant_key",
        "payfast_live_passphrase",
        "paypal_sandbox_client_id",
        "paypal_sandbox_secret",
        "paypal_live_client_id",
        "paypal_live_secret",
      ].forEach((k) => {
        if (form[k] && form[k].trim()) payload[k] = form[k].trim();
      });
      await adminSavePaymentSettings(payload);
      toast.success("Payment settings saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!s) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-payments-panel">
      {/* Plan + global mode */}
      <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-5">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-stone-600" strokeWidth={1.5} />
          <h3 className="font-display text-xl text-stone-900">Subscription plan</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Monthly price
            </label>
            <Input
              type="number"
              step="0.01"
              value={form.subscription_price}
              onChange={(e) => update("subscription_price", e.target.value)}
              data-testid="pay-price"
              className="h-10"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Currency
            </label>
            <Input
              value={form.currency}
              onChange={(e) => update("currency", e.target.value.toUpperCase())}
              data-testid="pay-currency"
              placeholder="ZAR"
              className="h-10"
            />
          </div>
        </div>
        <p className="text-xs text-stone-500" data-testid="pay-plan-note">
          Members pay per period (no auto-recurring) — they renew each month/year themselves.
          {" "}Annual is auto-priced at <strong>{form.currency} {(Number(form.subscription_price || 0) * 11).toFixed(2)}</strong> (pay 11 months, get 12 — 1 month free).
        </p>
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input
            type="checkbox"
            checked={form.live_mode}
            onChange={(e) => update("live_mode", e.target.checked)}
            data-testid="pay-live-mode"
          />
          Live mode (uncheck for sandbox/testing)
        </label>
        {form.currency !== "USD" && form.paypal_enabled && (
          <p className="text-xs text-amber-600" data-testid="pay-paypal-currency-warning">
            Note: PayPal does not support {form.currency}. Use a PayPal-supported currency
            (e.g. USD, EUR, GBP) for PayPal to work.
          </p>
        )}
      </div>

      {/* PayFast */}
      <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-4">
        <label className="flex items-center gap-2 text-stone-900 font-display text-xl">
          <input
            type="checkbox"
            checked={form.payfast_enabled}
            onChange={(e) => update("payfast_enabled", e.target.checked)}
            data-testid="pay-payfast-enabled"
          />
          PayFast
        </label>
        <p className="text-[10px] uppercase tracking-[0.15em] text-stone-400 font-semibold">Sandbox</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SecretField label="Merchant ID" testid="pf-sandbox-id" value={form.payfast_sandbox_merchant_id} set={s.payfast_sandbox_merchant_id_set} onChange={(v) => update("payfast_sandbox_merchant_id", v)} placeholder="10000100" />
          <SecretField label="Merchant Key" testid="pf-sandbox-key" value={form.payfast_sandbox_merchant_key} set={s.payfast_sandbox_merchant_key_set} onChange={(v) => update("payfast_sandbox_merchant_key", v)} />
          <SecretField label="Passphrase" testid="pf-sandbox-pass" value={form.payfast_sandbox_passphrase} set={s.payfast_sandbox_passphrase_set} onChange={(v) => update("payfast_sandbox_passphrase", v)} />
        </div>
        <p className="text-[10px] uppercase tracking-[0.15em] text-stone-400 font-semibold">Live</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SecretField label="Merchant ID" testid="pf-live-id" value={form.payfast_live_merchant_id} set={s.payfast_live_merchant_id_set} onChange={(v) => update("payfast_live_merchant_id", v)} />
          <SecretField label="Merchant Key" testid="pf-live-key" value={form.payfast_live_merchant_key} set={s.payfast_live_merchant_key_set} onChange={(v) => update("payfast_live_merchant_key", v)} />
          <SecretField label="Passphrase" testid="pf-live-pass" value={form.payfast_live_passphrase} set={s.payfast_live_passphrase_set} onChange={(v) => update("payfast_live_passphrase", v)} />
        </div>
      </div>

      {/* PayPal */}
      <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-4">
        <label className="flex items-center gap-2 text-stone-900 font-display text-xl">
          <input
            type="checkbox"
            checked={form.paypal_enabled}
            onChange={(e) => update("paypal_enabled", e.target.checked)}
            data-testid="pay-paypal-enabled"
          />
          PayPal
        </label>
        <p className="text-[10px] uppercase tracking-[0.15em] text-stone-400 font-semibold">Sandbox</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SecretField label="Client ID" testid="pp-sandbox-id" value={form.paypal_sandbox_client_id} set={s.paypal_sandbox_client_id_set} onChange={(v) => update("paypal_sandbox_client_id", v)} />
          <SecretField label="Secret" testid="pp-sandbox-secret" value={form.paypal_sandbox_secret} set={s.paypal_sandbox_secret_set} onChange={(v) => update("paypal_sandbox_secret", v)} />
        </div>
        <p className="text-[10px] uppercase tracking-[0.15em] text-stone-400 font-semibold">Live</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SecretField label="Client ID" testid="pp-live-id" value={form.paypal_live_client_id} set={s.paypal_live_client_id_set} onChange={(v) => update("paypal_live_client_id", v)} />
          <SecretField label="Secret" testid="pp-live-secret" value={form.paypal_live_secret} set={s.paypal_live_secret_set} onChange={(v) => update("paypal_live_secret", v)} />
        </div>
      </div>

      {/* EFT (manual bank transfer) */}
      <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-4">
        <label className="flex items-center gap-2 text-stone-900 font-display text-xl">
          <input
            type="checkbox"
            checked={form.eft_enabled}
            onChange={(e) => update("eft_enabled", e.target.checked)}
            data-testid="pay-eft-enabled"
          />
          EFT / Bank transfer
        </label>
        <p className="text-xs text-stone-500">
          These bank details are shown to users so they can pay by transfer. You'll confirm
          received payments under the “EFT Payments” tab.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">Bank name</label>
            <Input value={form.eft_bank_name} onChange={(e) => update("eft_bank_name", e.target.value)} data-testid="eft-bank-name" className="h-10" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">Account holder</label>
            <Input value={form.eft_account_name} onChange={(e) => update("eft_account_name", e.target.value)} data-testid="eft-account-name" className="h-10" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">Account number</label>
            <Input value={form.eft_account_number} onChange={(e) => update("eft_account_number", e.target.value)} data-testid="eft-account-number" className="h-10" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">Branch / routing code</label>
            <Input value={form.eft_branch_code} onChange={(e) => update("eft_branch_code", e.target.value)} data-testid="eft-branch-code" className="h-10" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">Account type</label>
            <Input value={form.eft_account_type} onChange={(e) => update("eft_account_type", e.target.value)} data-testid="eft-account-type" placeholder="Cheque / Savings" className="h-10" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">SWIFT / BIC (optional)</label>
            <Input value={form.eft_swift} onChange={(e) => update("eft_swift", e.target.value)} data-testid="eft-swift" className="h-10" />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">Payment reference</label>
          <Select value={form.eft_reference_mode} onValueChange={(v) => update("eft_reference_mode", v)}>
            <SelectTrigger data-testid="eft-reference-mode" className="h-10 w-full sm:w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="generated">Generate a unique reference</SelectItem>
              <SelectItem value="email">Use the user's email address</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>


      <Button
        onClick={save}
        disabled={busy}
        data-testid="pay-save"
        className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
        ) : (
          <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
        )}
        Save payment settings
      </Button>
    </div>
  );
}


function EftPaymentsPanel() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const confirmDialog = useConfirm();

  async function load() {
    setLoading(true);
    try {
      const data = await adminListEftPayments(status);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function confirm(id) {
    setConfirming(id);
    try {
      await adminConfirmEftPayment(id);
      toast.success("Payment confirmed — subscription activated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setConfirming(null);
    }
  }

  async function reject(id) {
    const ok = await confirmDialog({
      title: "Reject this payment?",
      description: "Mark this EFT as not received. The member will not be granted access.",
      confirmLabel: "Reject payment",
      destructive: true,
    });
    if (!ok) return;
    setRejecting(id);
    try {
      await adminRejectEftPayment(id);
      toast.success("Payment rejected (not received)");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRejecting(null);
    }
  }

  function statusBadge(st) {
    const map = {
      completed: ["bg-green-50 text-green-700", "Confirmed"],
      processing: ["bg-blue-50 text-blue-700", "Processing"],
      pending: ["bg-amber-50 text-amber-700", "Outstanding"],
      rejected: ["bg-red-50 text-red-700", "Rejected"],
      canceled: ["bg-stone-100 text-stone-500", "Canceled"],
    };
    const [cls, label] = map[st] || ["bg-amber-50 text-amber-700", "Outstanding"];
    return <span className={"text-xs px-2 py-0.5 rounded-full " + cls}>{label}</span>;
  }

  return (
    <div className="space-y-5 max-w-4xl" data-testid="admin-eft-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Banknote className="w-5 h-5 text-stone-700" strokeWidth={1.5} />
          <h3 className="font-display text-xl text-stone-900">EFT payments</h3>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="eft-status-filter" className="h-9 w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Outstanding</SelectItem>
            <SelectItem value="completed">Confirmed</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-5 h-5 animate-spin text-stone-400" strokeWidth={1.5} />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-stone-400 text-sm" data-testid="eft-empty">
          No {status === "pending" ? "outstanding" : status === "completed" ? "confirmed" : ""} EFT payments.
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-stone-400 border-b border-stone-100">
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Reference</th>
                <th className="px-4 py-3 font-semibold">Amount</th>
                <th className="px-4 py-3 font-semibold">Discount</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-stone-50 last:border-0" data-testid={`eft-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-stone-900">{p.user_name || "—"}</div>
                    <div className="text-stone-400 text-xs">{p.user_email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-700">{p.reference}</td>
                  <td className="px-4 py-3 text-stone-900" data-testid={`eft-amount-${p.id}`}>
                    {p.voucher_code && p.original_amount ? (
                      <div className="flex flex-col leading-tight">
                        <span className="text-stone-400 line-through text-xs">
                          {p.currency} {Number(p.original_amount).toFixed(2)}
                        </span>
                        <span className="font-medium">
                          {p.currency} {Number(p.amount).toFixed(2)}
                        </span>
                      </div>
                    ) : (
                      <>{p.currency} {Number(p.amount).toFixed(2)}</>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`eft-discount-${p.id}`}>
                    {p.voucher_code ? (
                      <span className="inline-flex flex-col gap-0.5">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold w-fit">
                          {p.voucher_code}
                        </span>
                        {p.discount_amount ? (
                          <span className="text-[11px] text-emerald-700">
                            −{p.currency} {Number(p.discount_amount).toFixed(2)}
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-stone-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-stone-500 text-xs">
                    {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {statusBadge(p.status)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.status === "completed" ? (
                      <span className="text-green-600 inline-flex items-center gap-1 text-xs">
                        <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Done
                      </span>
                    ) : p.status === "rejected" ? (
                      <span className="text-red-500 inline-flex items-center gap-1 text-xs">
                        <XCircle className="w-4 h-4" strokeWidth={1.5} /> Rejected
                      </span>
                    ) : p.status === "canceled" ? (
                      <span className="text-stone-400 text-xs">Canceled by user</span>
                    ) : (
                      <div className="inline-flex items-center gap-2 justify-end">
                        <Button
                          size="sm"
                          onClick={() => confirm(p.id)}
                          disabled={confirming === p.id || rejecting === p.id}
                          data-testid={`eft-confirm-${p.id}`}
                          className="rounded-full bg-stone-900 text-white hover:bg-stone-800 h-8"
                        >
                          {confirming === p.id ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={1.5} />
                          ) : (
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                          )}
                          Payment received
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reject(p.id)}
                          disabled={confirming === p.id || rejecting === p.id}
                          data-testid={`eft-reject-${p.id}`}
                          className="rounded-full h-8 border-red-200 text-red-600 hover:bg-red-50"
                        >
                          {rejecting === p.id ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={1.5} />
                          ) : (
                            <XCircle className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                          )}
                          Not received
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

